// Canonical display of plain numbers in Danish notation: "." for thousands,
// "," for the decimal separator.
//
// Replaces two competing idioms that were scattered across the company grids
// and rating cards — `n.toLocaleString("da-DK")` and
// `n.toFixed(1).replace(".", ",")` — which agree on Danish output but drift
// apart the moment a call site needs a different precision.

const INTEGER = new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 });
// Trailing ".0" is dropped, so 1.000 reads "1k" rather than "1,0k".
const COMPACT = new Intl.NumberFormat("da-DK", { maximumFractionDigits: 1 });

/** "4,5" — a score or average. One decimal unless the caller asks otherwise. */
export function formatDecimal(value: number, digits = 1): string {
  return value.toLocaleString("da-DK", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** "1.234" — a whole count. */
export function formatCount(value: number): string {
  return INTEGER.format(value);
}

/**
 * "1,2k" from a thousand up, "834" below it. For review counts, where the
 * exact figure past the first significant digits is noise and the badge it
 * sits in is narrow.
 */
export function formatCompactCount(value: number): string {
  if (value >= 1000) return `${COMPACT.format(Math.round(value / 100) / 10)}k`;
  return formatCount(value);
}

/**
 * "62%" from a 0..1 ratio, or an en dash when the ratio is undefined — a rate
 * with no denominator yet is not 0%, and the admin cards must not imply it is.
 */
export function formatPercent(ratio: number | null): string {
  return ratio === null ? "–" : `${Math.round(ratio * 100)}%`;
}
